import { useEffect, useState } from "react";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { Navigate } from "react-router-dom";

import { db } from "@/firebase";
import Layout from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import Loader from "@/components/Loader";

/**
 * Auth / Login Activity Logs
 * Admin & Super Admin only
 */

export default function AuthLogs() {
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);

  // 🔐 get user from localStorage (same pattern as Logs.jsx)
  const user = JSON.parse(localStorage.getItem("user"));

  // -------------------------
  // 🔐 ROLE PROTECTION
  // -------------------------
  if (!user) {
    return <Navigate to="/login" />;
  }

  if (!["admin", "super_admin"].includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }

  // -------------------------
  // 🔥 REALTIME AUTH LOGS
  // -------------------------
  useEffect(() => {
    const q = query(
      collection(db, "auth_logs"),
      orderBy("created_at", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setLogs(list);
        setLoadingLogs(false);
      },
      (err) => {
        console.error("Auth logs snapshot error:", err);
        setLoadingLogs(false);
      }
    );

    return () => unsub();
  }, []);

  // -------------------------
  // 🎨 ACTION COLOR HELPER
  // -------------------------
  const getColor = (action) => {
    switch (action) {
      case "login":
        return "bg-green-100 text-green-800 border-green-200";
      case "logout":
        return "bg-gray-100 text-gray-800 border-gray-200";
      case "failed_login":
        return "bg-red-100 text-red-800 border-red-200";
      case "blocked":
        return "bg-orange-100 text-orange-800 border-orange-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // -------------------------
  // 🖥️ UI
  // -------------------------
  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Login Activity Logs</h1>
          <p className="text-sm text-gray-500">
            Track user authentication attempts and sessions
          </p>
        </div>

        {loadingLogs ? (
          <Loader />
        ) : logs.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            No login activity found
          </div>
        ) : (
          <div className="space-y-3">
            {logs.map((log) => (
              <div
                key={log.id}
                className="border rounded-lg p-4 bg-white hover:shadow-sm transition"
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="space-y-1">
                    <p className="font-medium">
                      {log.email || "Unknown User"}
                    </p>

                    <p className="text-sm text-gray-700">
                      {log.message}
                    </p>

                    <p className="text-xs text-gray-500">
                      IP: {log.ip || "N/A"} | Device: {log.device || "N/A"}
                    </p>

                    <p className="text-xs text-gray-400">
                      {log.created_at?.toDate
                        ? log.created_at.toDate().toLocaleString()
                        : ""}
                    </p>
                  </div>

                  <Badge
                    variant="outline"
                    className={getColor(log.action)}
                  >
                    {log.action}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
